/**
 * V4.36 — Carte "Utilisation de Crédit Flow" dans Pilotage.
 *
 * Doctrine : "comprendre comment j'utilise réellement l'application
 *             avant d'ajouter de nouvelles fonctionnalités."
 *
 * Safe = Silence : si total_events = 0, la carte affiche un état d'attente discret.
 */
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { ChartBar, Trophy, Sparkle, Phone, ArrowRight } from "@phosphor-icons/react";

const PERIODS = [
  { value: 7, label: "7j" },
  { value: 30, label: "30j" },
  { value: 90, label: "90j" },
];

const CATEGORY_COLOR = {
  commando: "var(--cf-primary)",
  dossiers: "var(--cf-accent)",
  pilotage: "var(--cf-info)",
};

function CompactBar({ value, max, color }) {
  const pct = max > 0 ? Math.max(2, (value / max) * 100) : 0;
  return (
    <div className="w-full h-1.5 rounded-full bg-slate-100 overflow-hidden">
      <div className="h-full rounded-full transition-all" style={{ width: `${pct}%`, background: color }} />
    </div>
  );
}

export default function UsageCard() {
  const navigate = useNavigate();
  const [days, setDays] = useState(30);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    api.get(`/usage/summary?days=${days}`).then((r) => {
      if (!cancelled) setData(r.data);
    }).catch((e) => {
      if (!cancelled) setError(e?.message || String(e));
    }).finally(() => {
      if (!cancelled) setLoading(false);
    });
    return () => { cancelled = true; };
  }, [days]);

  if (loading && !data) {
    return (
      <section className="card-base p-5 mb-4" data-testid="usage-card">
        <div className="text-sm font-bold text-slate-400">Chargement de l&apos;usage…</div>
      </section>
    );
  }
  if (error) {
    return (
      <section className="card-base p-5 mb-4" data-testid="usage-card">
        <div className="text-sm font-bold text-rose-500">Erreur usage : {error}</div>
      </section>
    );
  }
  if (!data) return null;

  const maxCount = Math.max(1, ...data.counts_by_event.map((e) => e.count));
  const isEmpty = data.total_events === 0;

  return (
    <section className="card-base p-5 mb-4" data-testid="usage-card">
      <div className="flex items-center gap-2 mb-3">
        <ChartBar size={18} weight="fill" color="var(--cf-accent)" />
        <h2 className="font-display text-lg md:text-xl font-extrabold text-slate-900">
          Utilisation de Crédit Flow
        </h2>
        <div className="ml-auto inline-flex items-center rounded-full bg-slate-100 p-0.5" role="tablist" data-testid="usage-period-toggle">
          {PERIODS.map((p) => (
            <button
              key={p.value}
              type="button"
              onClick={() => setDays(p.value)}
              className={`px-2.5 py-1 rounded-full text-[11px] font-extrabold tabular-nums transition-colors ${
                days === p.value ? "bg-slate-900 text-white" : "text-slate-500 hover:text-slate-700"
              }`}
              data-testid={`usage-period-${p.value}`}
            >
              {p.label}
            </button>
          ))}
        </div>
      </div>

      {/* Safe = Silence : si aucune donnée, message d'attente discret */}
      {isEmpty ? (
        <div
          className="rounded-lg bg-slate-50 px-3 py-4 text-center text-[12px] text-slate-500 font-semibold"
          data-testid="usage-empty"
        >
          Pas encore d&apos;usage enregistré sur cette période. Continue à utiliser Crédit Flow — les statistiques apparaîtront ici dans quelques heures.
        </div>
      ) : (
        <>
          {/* Top actions */}
          {data.top_actions.length > 0 && (
            <div className="mb-4" data-testid="usage-top-actions">
              <div className="text-[10px] font-extrabold text-slate-400 uppercase tracking-wider mb-2 flex items-center gap-1">
                <Trophy size={12} weight="fill" color="var(--cf-warning)" /> Top actions
              </div>
              <div className="space-y-2">
                {data.top_actions.map((a) => (
                  <div key={a.event_type} className="flex items-center gap-3" data-testid={`usage-top-${a.event_type}`}>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between text-[12px] font-bold text-slate-700">
                        <span className="truncate">{a.label}</span>
                        <span className="tabular-nums text-slate-900 font-extrabold">{a.count}</span>
                      </div>
                      <CompactBar value={a.count} max={maxCount} color={CATEGORY_COLOR[a.category] || "var(--cf-text-3)"} />
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Répartition appels / WA / email */}
          <div className="mb-4 pb-4 border-b border-slate-100" data-testid="usage-contact-breakdown">
            <div className="text-[10px] font-extrabold text-slate-400 uppercase tracking-wider mb-2 flex items-center gap-1">
              <Phone size={12} weight="fill" color="var(--cf-primary)" /> Répartition des contacts
            </div>
            <div className="flex items-center gap-3 text-[12px]">
              {data.contact_actions_breakdown.map((c) => (
                <div key={c.event_type} className="flex-1" data-testid={`usage-contact-${c.event_type}`}>
                  <div className="flex items-center justify-between">
                    <span className="text-slate-500 font-semibold">{c.label.replace(" lancés", "").replace(" générés", "")}</span>
                    <span className="tabular-nums font-extrabold text-slate-900">{c.count}</span>
                  </div>
                  <div className="text-[10px] text-slate-400 font-bold tabular-nums">{c.pct}%</div>
                </div>
              ))}
            </div>
          </div>

          {/* KPIs secondaires */}
          <div className="grid grid-cols-2 gap-3 mb-4" data-testid="usage-kpis">
            <div className="rounded-lg bg-slate-50 px-3 py-2">
              <div className="text-[10px] font-extrabold text-slate-400 uppercase tracking-wider">Dossiers via Commando</div>
              <div className="text-xl font-extrabold text-slate-900 tabular-nums" data-testid="usage-dossiers-commando-count">
                {data.dossiers_via_commando_count}
              </div>
            </div>
            <div className="rounded-lg bg-slate-50 px-3 py-2">
              <div className="text-[10px] font-extrabold text-slate-400 uppercase tracking-wider">Délai moyen traitement relance</div>
              <div className="text-xl font-extrabold text-slate-900 tabular-nums" data-testid="usage-avg-response">
                {data.avg_relance_response_days != null ? `${data.avg_relance_response_days}j` : "—"}
              </div>
            </div>
          </div>

          {/* Top 5 dossiers */}
          {data.top_dossiers.length > 0 && (
            <div className="mb-4" data-testid="usage-top-dossiers">
              <div className="text-[10px] font-extrabold text-slate-400 uppercase tracking-wider mb-2">
                Top 5 dossiers consultés
              </div>
              <div className="space-y-1.5">
                {data.top_dossiers.map((d) => (
                  <button
                    type="button"
                    key={d.dossier_id}
                    onClick={() => navigate(`/dossier/${d.dossier_id}`)}
                    className="w-full flex items-center gap-2 px-2 py-1.5 rounded-lg hover:bg-slate-50 transition-colors text-left"
                    data-testid={`usage-top-dossier-${d.dossier_id}`}
                  >
                    <div className="flex-1 min-w-0">
                      <div className="text-[12px] font-bold text-slate-900 truncate">{d.nom}</div>
                      {d.apporteur && (
                        <div className="text-[10px] text-slate-500 font-semibold truncate">{d.apporteur}</div>
                      )}
                    </div>
                    <div className="tabular-nums text-[11px] font-extrabold text-slate-700">{d.count}×</div>
                    <ArrowRight size={12} className="text-slate-300" />
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Top 5 apporteurs */}
          {data.top_apporteurs.length > 0 && (
            <div className="mb-4" data-testid="usage-top-apporteurs">
              <div className="text-[10px] font-extrabold text-slate-400 uppercase tracking-wider mb-2">
                Top 5 apporteurs consultés
              </div>
              <div className="space-y-1">
                {data.top_apporteurs.map((a) => (
                  <div
                    key={a.apporteur}
                    className="flex items-center justify-between px-2 py-1.5 rounded-lg bg-slate-50 text-[12px]"
                    data-testid={`usage-top-apporteur-${a.apporteur.replace(/\s+/g, "-")}`}
                  >
                    <span className="font-bold text-slate-700 truncate">{a.apporteur}</span>
                    <span className="tabular-nums font-extrabold text-slate-900">{a.count}×</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Fonctionnalités jamais utilisées */}
          {data.unused_actions.length > 0 && (
            <div className="pt-3 border-t border-slate-100" data-testid="usage-unused">
              <div className="text-[10px] font-extrabold text-amber-600 uppercase tracking-wider mb-2 flex items-center gap-1">
                <Sparkle size={12} weight="fill" /> Jamais utilisée
              </div>
              <div className="flex flex-wrap gap-1.5">
                {data.unused_actions.map((u) => (
                  <span
                    key={u.event_type}
                    className="px-2 py-0.5 rounded-full bg-amber-50 text-amber-700 text-[10.5px] font-bold border border-amber-100"
                    data-testid={`usage-unused-${u.event_type}`}
                    title={u.event_type}
                  >
                    {u.label}
                  </span>
                ))}
              </div>
            </div>
          )}

          {/* Footer total */}
          <div className="mt-3 pt-3 border-t border-slate-100 text-[10.5px] text-slate-400 font-semibold flex items-center justify-between">
            <span>Période : {data.period_start} → {data.period_end}</span>
            <span>
              <span className="tabular-nums font-bold text-slate-700">{data.total_events}</span> événement{data.total_events > 1 ? "s" : ""} enregistré{data.total_events > 1 ? "s" : ""}
            </span>
          </div>
        </>
      )}
    </section>
  );
}
